import {
    Box,
    Text,
    Flex,
    Stat,
    StatLabel,
    useColorModeValue,
    Alert,
    AlertIcon,
    AlertTitle,
} from '@chakra-ui/react'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts'
import React from 'react'

interface ChartPoint {
    interval: string
    value: number
}

interface DeviceData {
    title: string
    datas: ChartPoint[]
    parameter_title: string
    measurement_unit: string
    total_value: number
}

interface LineChartCardProps {
    data: DeviceData[]
}

const LineChartCard: React.FC<LineChartCardProps> = ({ data }) => {
    const bg = useColorModeValue('white', 'gray.800')

    if (!data || data.length === 0) {
        return (
            <Flex justify="center" align="center" h="100%">
                <Alert
                    status="info"
                    bg="blue.50"
                    borderRadius="md"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    height="100%"
                    color="blue.800"
                >
                    <AlertIcon boxSize="30px" mr={0} color="blue.500" />
                    <AlertTitle mt={2} mb={1} fontSize="md" fontWeight="bold">
                        No data available
                    </AlertTitle>
                </Alert>
            </Flex>
        )
    }

    const unit = data[0].measurement_unit ?? ''
    const parameterTitle = data[0].parameter_title ?? 'Parameter'

    const chartDataMap = new Map<string, Record<string, any>>()
    const deviceLineKeys: string[] = []

    data.forEach((device, i) => {
        const lineKey = `${device.title} (${i})`
        deviceLineKeys.push(lineKey)

        device.datas.forEach((point) => {
            const dateKey = point.interval
            if (!chartDataMap.has(dateKey)) {
                chartDataMap.set(dateKey, { interval: dateKey })
            }
            chartDataMap.get(dateKey)![lineKey] = point.value
        })
    })

    chartDataMap.forEach((record) => {
        deviceLineKeys.forEach((key) => {
            if (record[key] === undefined) {
                record[key] = 0
            }
        })
    })

    const chartData = Array.from(chartDataMap.values()).sort(
        (a, b) => new Date(a.interval).getTime() - new Date(b.interval).getTime()
    )

    const totalValue = data.reduce((sum, d) => sum + (d.total_value ?? 0), 0)

    return (
        <Box bg={bg} p={4} borderRadius="lg" boxShadow="sm" w="100%">
            <Flex justify="space-between" align="center" mb={3}>
                <Stat>
                    <StatLabel fontWeight={700} fontSize={14} color="#484848">
                        {parameterTitle}
                    </StatLabel>
                    <Flex gap={2} alignItems="center" fontWeight={700} fontSize="24px" color="#484848">
                        {totalValue.toLocaleString()}
                        <Text as="span" fontSize="15px" color="#9B9B9B" fontWeight={400}>
                            {unit}
                        </Text>
                    </Flex>
                </Stat>
            </Flex>

            <Box w="100%" h="80%">
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.1} />
                            <XAxis
                                dataKey="interval"
                                fontSize={10}
                                tickFormatter={(date) => new Date(date).toLocaleDateString('bg-BG')}
                            />
                            <YAxis fontSize={10} />
                            <Tooltip
                                formatter={(value: number) => value.toFixed(2)}
                                labelFormatter={(label: string) => {
                                    const date = new Date(label)
                                    return date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'short', year: 'numeric' })
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey={deviceLineKeys[0]}
                                stroke="#3366CC"
                                strokeWidth={2}
                                dot={false}
                            />
                            {deviceLineKeys[1] && (
                                <Line
                                    type="monotone"
                                    dataKey={deviceLineKeys[1]}
                                    stroke="#FF9900"
                                    strokeWidth={2}
                                    dot={false}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Text>No chart data</Text>
                )}
            </Box>
        </Box>
    )
}

export default LineChartCard
